/**
 * RunProgress React Query cache helpers (ENG-367).
 *
 * Kept apart from useRunProgress so useLiveUpdates can write SSE snapshots
 * into the cache without an import cycle
 * (useRunProgress -> LiveUpdatesProvider -> useLiveUpdates).
 */

import type { QueryClient } from "@tanstack/react-query";
import { runKindSchema, runProgressSchema, type RunKind } from "@mycompbox/shared";

/** SSE event type carrying a RunProgress snapshot. */
export const RUN_PROGRESS_SSE_EVENT = "progress.updated";

export const runProgressKeys = {
  all: ["runProgress"] as const,
  detail: (kind: RunKind, id: string) => [...runProgressKeys.all, kind, id] as const,
};

/**
 * Write a `progress.updated` SSE payload into the cache. Returns true if the
 * payload was valid and applied. Malformed payloads are ignored instead of
 * corrupting the cache.
 */
export function applyRunProgressEvent(queryClient: QueryClient, data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const { kind, id, runProgress } = data as Record<string, unknown>;

  const parsedKind = runKindSchema.safeParse(kind);
  if (!parsedKind.success || typeof id !== "string" || id.length === 0) return false;

  const parsedProgress = runProgressSchema.safeParse(runProgress);
  if (!parsedProgress.success) return false;

  queryClient.setQueryData(runProgressKeys.detail(parsedKind.data, id), parsedProgress.data);
  return true;
}

